import * as React from "react";
import { Button, Card } from "antd";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CardContent } from "../../components/antd/card/CardContent";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { User } from "../../schema/types/user/entity";
import { parseLink } from "../../utils/linkParser/linkParser";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableModal } from "../../components/antd/TableModal";
import { Table } from "../../newcomponents/table/table";
import { GroupInput } from "../../schema/types/group/input";
import { UserNameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/userNameDescriptionColumn";
import { UserNameDescriptionNoLinkColumn } from "../../newcomponents/table/columns/wellKnown/userNameDescriptionNoLinkColumn";
import { getUserTableItem, UserTableItem } from "../../mst/kinds/user.helpers";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";

interface Props {}
export const UserItems: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const { kind: addTableKind, id: addTableId } = useTableKindId("user", "group-user-items-update-1");
  const qData = useTableItemQueryData<User>(addTableKind, {});

  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<UserTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, UserTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  const users = useCpFormValue(`users`);
  const userTableItems: UserTableItem[] = React.useMemo(() => users.map((u) => getUserTableItem(u)), [users]);

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: GroupInput = form.values as any;

    const currentUsers: KindSelectState[] = cloneDeep(values.users);
    currentUsers.splice(index, 1);
    formSetValue(id, `users`, currentUsers);

    formTriggerValidation(id);
  };

  function onOkAddModal() {
    const form = store.getState().form.instances[id];
    const values: GroupInput = form.values as any;

    const currentUsers: KindSelectState[] = cloneDeep(values.users);

    // TODO selectionsToAdd can be renamed to something with link
    for (const selectionToAdd of selectionsToAdd) {
      const currentLinks = currentUsers.map((u) => u.link);
      const item = selectionItems.find((i) => i.selfLink === selectionToAdd);
      if (item) {
        itemCache[selectionToAdd] = item;
      }

      if (currentLinks.includes(selectionToAdd)) {
        continue;
      }

      const { name } = parseLink(selectionToAdd, { useInputCtx: true });
      currentUsers.push({
        value: name,
        item: item,
        link: selectionToAdd,
      });
    }
    // TODO was sorting the links but no more because now it is kindselectstate

    // TODO initialize this item cache on load for detail page
    setItemCache(itemCache);

    formSetValue(id, `users`, currentUsers);

    setSelectionsToAdd([]);
    setSelectionItems([]);
    setIsAddingItem(false);

    formTriggerValidation(id);
  }

  const currentLinks = users.map((u) => u.link);
  const addUserTableItems: UserTableItem[] = qData.visibleItems
    .map((item) => getUserTableItem({ item: item }))
    .filter((item) => !currentLinks.includes(item.selfLink));

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>Users</CardTitle>
          <CardContent>
            {userTableItems.length > 0 ? (
              <Table<UserTableItem>
                tableId="group-user-items-editable-update2"
                data={userTableItems}
                hideSettings
                columns={[
                  UserNameDescriptionNoLinkColumn({ enableSort: false }),
                  TagsColumn(),
                  CreatedColumn(),
                  LastModifiedColumn(),
                  {
                    id: "action",
                    label: "",
                    enableResize: false,
                    size: 120,
                    cell: ({ row }) => {
                      return (
                        <div className="w-full flex justify-end">
                          <Button
                            htmlType={`button`}
                            type={"text"}
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => onRemoveItem(row.index)}
                          />
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : null}
            <TableModal
              open={isAddingItem}
              onCancel={() => setIsAddingItem(false)}
              onOk={() => {
                onOkAddModal();
              }}
            >
              <Table<UserTableItem>
                tableId={addTableId}
                title={"Select Users to Add"}
                selectMode="multi"
                selectKey="selfLink"
                selections={selectionsToAdd}
                onSelectionsChange={(keys, items) => {
                  setSelectionsToAdd(keys);
                  setSelectionItems(items!);
                }}
                data={addUserTableItems}
                headerRenderer={() => {
                  return (
                    <>
                      <TableHeaderRefreshButton onClick={qData.fetchItems} />
                      <CustomTableCPLNQuery
                        tableId={addTableId}
                        kind={addTableKind}
                        onQuery={qData.setQuery}
                        isLoading={qData.isLoadingQueried}
                      />
                    </>
                  );
                }}
                enableFilter
                isLoading={qData.isLoading}
                onSortChange={qData.setSort}
                onSearchChange={qData.setSearch}
                isLoadingMore={qData.isLoadingMore}
                hasMore={qData.hasMore}
                onLoadMore={qData.fetchNextPage}
                columns={[
                  UserNameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
                  TagsColumn(),
                  CreatedColumn(),
                  LastModifiedColumn(),
                ]}
              />
            </TableModal>
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => setIsAddingItem(true)}
            >
              Add User
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
